import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Button,
  useToast,
  Divider,
  SimpleGrid,
  Image,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();

  const handleReadMore = (e) => {
    const buttonId = e.currentTarget.id; // Get the button's id
    const [blogTitle, blogId] = buttonId.split("-"); // Split the id to extract title and ID
    // Navigate to the blog details page using the blog title
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/blogs/${encodeURIComponent(blogTitle)}`, {
      state: { id: blogId },
    });
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(
          "https://api.qualiteeth.co.in/api/blogList"
        ); // Replace with your API endpoint
        // const response = await fetch("http://localhost:5000/api/blogList");
        if (!response.ok) {
          throw new Error("Failed to fetch blogs.");
        }
        const data = await response.json();

        // Ensure `blogs` data exists in the expected format
        if (Array.isArray(data.Blog)) {
          setBlogs(data.Blog);
        } else {
          throw new Error("Unexpected data format.");
        }
      } catch (err) {
        setError(err.message || "Something went wrong.");
        toast({
          title: "Error",
          description: err.message || "Failed to fetch blogs.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, [toast]);

  const truncateText = (text, wordLimit) => {
    const words = text.split(" ");
    return words.length > wordLimit
      ? `${words.slice(0, wordLimit).join(" ")}...`
      : text;
  };

  // Rendered in every branch below. If it only lived in the success return,
  // a slow or failing API would leave the page wearing the homepage's title
  // and canonical URL — and the prerenderer would bake that in.
  const seo = (
    <Seo
      path="/blog"
      title="Dental Health Blog | Qualiteeth Dental Care, Jaipur"
      description="Dental health advice from the dentists at Qualiteeth Dental Care, Vaishali Nagar, Jaipur — treatment guides, oral hygiene tips and answers to common questions."
      breadcrumbs={[
        { name: "Home", path: "/" },
        { name: "Blog", path: "/blog" },
      ]}
    />
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100vh"
      >
        {seo}
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6} marginTop="80px" bg="#fceee5">
        {seo}
        <Heading as="h1" size="xl" mb={4}>
          Dental Health Blog
        </Heading>
        <Text>We couldn't load the articles just now. Please try again shortly.</Text>
      </Box>
    );
  }

  return (
    <Box>
      {seo}
      <Box py={8} px={4} marginTop="80px" bg="#fceee5">
        <Heading
          as="h1"
          fontSize={{ md: "4xl", base: "2xl" }}
          mb={10}
          fontWeight="light"
          textAlign="center"
        >
          Healthy Teeth, Happy Life: Insights from Our Dentists
        </Heading>
        {blogs.length === 0 ? (
          <Text
            textAlign="center"
            fontSize="lg"
            fontWeight="light"
            color="gray.600"
          >
            No blogs available.
          </Text>
        ) : (
          <SimpleGrid columns={[1, 2, 3]} spacing={6}>
            {blogs.map((blog) => (
              <Box
                key={blog.id}
                bg="#28211d"
                p={4}
                borderWidth={1}
                borderRadius="md"
                boxShadow="md"
                _hover={{
                  transform: "translateY(-20px)",
                  borderRadius: "md",
                  boxShadow: "lg",
                  bg: "#3A312B",
                }}
                transition="all 0.3s"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="100%" // Ensures consistent height for all cards
              >
                <Box
                  id={`${blog.title}-${blog.id}`}
                  onClick={(e) => handleReadMore(e)}
                >
                  <Image
                    src={blog.cover_image} // Assuming the image URL is in blog.coverImage
                    alt={blog.title}
                    loading="lazy"
                    borderRadius="md"
                    mb={4}
                    objectFit="cover"
                    height={{ base: "150px", md: "200px" }} // Adjusts height based on screen size
                    width="100%" // Makes the image responsive
                  />
                  <Heading
                    as="h2"
                    fontWeight="light"
                    fontSize={{ base: "lg", md: "xl" }}
                    mb={3}
                    color="#F5C7A9"
                  >
                    {blog.title}
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="#F5C7A9"
                    mb={4}
                    fontStyle="italic"
                  >
                    {`By ${blog.author} - ${new Date(
                      blog.created_at
                    ).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}`}
                  </Text>
                  <Divider mb={4} />
                  <Text color="#F5C7A9" fontWeight="light">
                    {truncateText(
                      blog.content.map((section) => section.text).join(" "),
                      40
                    )}
                  </Text>
                </Box>

                <Box mt={4} textAlign="right">
                  <Button
                    as="a"
                    id={`${blog.title}-${blog.id}`}
                    onClick={(e) => handleReadMore(e)}
                    bg="#FDC6A5"
                    size="sm"
                    _hover={{ bg: "#FCA982" }}
                  >
                    Read More
                  </Button>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
};

export default BlogList;
